import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Pagination,
  Divider,
  Slider,
} from "@mui/material";
import { tokens } from "../../theme";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MicIcon from "@mui/icons-material/Mic";
import DrawIcon from "@mui/icons-material/Draw";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { base_url } from "../../config/config";

const API_BASE = `${base_url}/v1/simple-app`;

const SPEAKER_COLORS = {
  A: "#2196F3",
  B: "#4CAF50",
  AB: "#FF9800",
};

const VoiceContracts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    contract: null,
  });
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });

  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [selectedUser, page, dateFilter]);

  // Cleanup audio on dialog close
  useEffect(() => {
    if (!detailDialog.open && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      setAudioProgress(0);
    }
  }, [detailDialog.open]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/users`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
      });

      if (selectedUser) {
        params.append("createdBy", selectedUser);
      }
      if (dateFilter.startDate) {
        params.append("startDate", dateFilter.startDate);
      }
      if (dateFilter.endDate) {
        params.append("endDate", dateFilter.endDate);
      }

      const response = await fetch(`${API_BASE}/contracts?${params}`);
      const data = await response.json();

      if (data.success) {
        setContracts(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError("Fehler beim Laden der Gesprächsdokumentation");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (contract) => {
    try {
      const response = await fetch(`${API_BASE}/contracts/${contract._id}`);
      const data = await response.json();
      if (data.success) {
        setDetailDialog({ open: true, contract: data.data });
      } else {
        setDetailDialog({ open: true, contract });
      }
    } catch {
      setDetailDialog({ open: true, contract });
    }
  };

  const handleCloseDetails = () => {
    setDetailDialog({ open: false, contract: null });
  };

  // ---- Audio Controls ----

  const handlePlayPause = (audioUrl) => {
    if (!audioUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener("timeupdate", () => {
        setAudioProgress(audioRef.current.currentTime);
      });
      audioRef.current.addEventListener("loadedmetadata", () => {
        setAudioDuration(audioRef.current.duration);
      });
      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
        setAudioProgress(0);
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (_, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setAudioProgress(newValue);
    }
  };

  // ---- Helpers ----

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "draft":
        return "warning";
      case "archived":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "completed":
        return "Abgeschlossen";
      case "draft":
        return "Entwurf";
      case "archived":
        return "Archiviert";
      default:
        return status;
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4" color={colors.grey[100]}>
          Gesprächsdokumentation
        </Typography>

        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Benutzer filtern</InputLabel>
            <Select
              value={selectedUser}
              label="Benutzer filtern"
              onChange={(e) => {
                setSelectedUser(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">Alle Benutzer</MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.displayName || user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            size="small"
            label="Von"
            value={dateFilter.startDate}
            onChange={(e) => {
              setDateFilter({ ...dateFilter, startDate: e.target.value });
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            type="date"
            size="small"
            label="Bis"
            value={dateFilter.endDate}
            onChange={(e) => {
              setDateFilter({ ...dateFilter, endDate: e.target.value });
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: colors.primary[400] }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datum/Zeit</TableCell>
                  <TableCell>Erstellt von</TableCell>
                  <TableCell>Person A</TableCell>
                  <TableCell>Person B</TableCell>
                  <TableCell>Dauer</TableCell>
                  <TableCell>Segmente</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color={colors.grey[300]} py={4}>
                        Keine Gesprächsdokumentation gefunden.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contracts.map((contract) => (
                    <TableRow key={contract._id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(contract.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {contract.createdBy?.displayName ||
                            contract.createdBy?.name ||
                            "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<PersonIcon />}
                          label={contract.personAName}
                          size="small"
                          sx={{ borderLeft: `3px solid ${SPEAKER_COLORS.A}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<PersonIcon />}
                          label={contract.personBName}
                          size="small"
                          sx={{ borderLeft: `3px solid ${SPEAKER_COLORS.B}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTimeIcon
                            sx={{ fontSize: 16, color: colors.grey[300] }}
                          />
                          <Typography variant="body2">
                            {formatDuration(contract.recordingDuration)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<MicIcon />}
                          label={contract.transcript?.length || 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(contract.status)}
                          color={getStatusColor(contract.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleViewDetails(contract)}
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="secondary"
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {detailDialog.contract && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <MicIcon color="primary" />
                Sprachvertrag - {detailDialog.contract.personAName} &{" "}
                {detailDialog.contract.personBName}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                {/* Meta Info */}
                <Box display="flex" gap={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Erstellt von
                    </Typography>
                    <Typography fontWeight="bold">
                      {detailDialog.contract.createdBy?.displayName ||
                        detailDialog.contract.createdBy?.name ||
                        "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Datum/Zeit
                    </Typography>
                    <Typography>
                      {formatDate(detailDialog.contract.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Dauer
                    </Typography>
                    <Typography>
                      {formatDuration(detailDialog.contract.recordingDuration)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Status
                    </Typography>
                    <Chip
                      label={getStatusLabel(detailDialog.contract.status)}
                      color={getStatusColor(detailDialog.contract.status)}
                      size="small"
                    />
                  </Box>
                </Box>

                {/* Audio Player */}
                {detailDialog.contract.audioFile?.s3Url && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="h6"
                        color={colors.greenAccent[400]}
                        mb={1}
                      >
                        Audioaufnahme
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: colors.primary[500],
                          borderRadius: 2,
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <IconButton
                          onClick={() =>
                            handlePlayPause(
                              detailDialog.contract.audioFile.s3Url,
                            )
                          }
                          sx={{
                            backgroundColor: colors.greenAccent[500],
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: colors.greenAccent[600],
                            },
                          }}
                        >
                          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>

                        <Box flex={1}>
                          <Slider
                            value={audioProgress}
                            max={audioDuration || 1}
                            onChange={handleSeek}
                            size="small"
                            sx={{ color: colors.greenAccent[500] }}
                          />
                          <Box display="flex" justifyContent="space-between">
                            <Typography
                              variant="caption"
                              color={colors.grey[300]}
                            >
                              {formatDuration(audioProgress)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={colors.grey[300]}
                            >
                              {formatDuration(audioDuration)}
                            </Typography>
                          </Box>
                        </Box>

                        <IconButton
                          onClick={() =>
                            window.open(
                              detailDialog.contract.audioFile.s3Url,
                              "_blank",
                            )
                          }
                          size="small"
                          title="Herunterladen"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </>
                )}

                {/* Transcript */}
                {detailDialog.contract.transcript?.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="h6"
                        color={colors.greenAccent[400]}
                        mb={1}
                      >
                        Gesprächsprotokoll (
                        {detailDialog.contract.transcript.length} Segmente)
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 400,
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {detailDialog.contract.transcript.map(
                          (segment, index) => {
                            const speakerName =
                              segment.speaker === "AB"
                                ? `${detailDialog.contract.personAName} & ${detailDialog.contract.personBName}`
                                : segment.speaker === "A"
                                  ? detailDialog.contract.personAName
                                  : detailDialog.contract.personBName;
                            const speakerColor =
                              SPEAKER_COLORS[segment.speaker] || "#999";

                            return (
                              <Box
                                key={index}
                                sx={{
                                  borderLeft: `4px solid ${speakerColor}`,
                                  pl: 2,
                                  py: 1,
                                  backgroundColor: colors.primary[500],
                                  borderRadius: "0 8px 8px 0",
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  mb={0.5}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ color: speakerColor }}
                                  >
                                    {speakerName}
                                  </Typography>
                                  {segment.timestamp > 0 && (
                                    <Typography
                                      variant="caption"
                                      color={colors.grey[400]}
                                    >
                                      {formatDuration(segment.timestamp)}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="body1">
                                  {segment.text}
                                </Typography>
                              </Box>
                            );
                          },
                        )}
                      </Box>
                    </Box>
                  </>
                )}

                {/* Signatures */}
                {(detailDialog.contract.signatureA ||
                  detailDialog.contract.signatureB) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="h6"
                        color={colors.greenAccent[400]}
                        mb={1}
                      >
                        <DrawIcon
                          sx={{
                            fontSize: 20,
                            mr: 0.5,
                            verticalAlign: "text-bottom",
                          }}
                        />
                        Unterschriften
                      </Typography>
                      <Box display="flex" gap={3} flexWrap="wrap">
                        {detailDialog.contract.signatureA && (
                          <Box>
                            <Typography
                              variant="body2"
                              color={colors.grey[300]}
                              mb={1}
                            >
                              {detailDialog.contract.personAName}
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: "#fff",
                                borderRadius: 2,
                                p: 1,
                                display: "inline-block",
                                cursor: "pointer",
                                border: `2px solid ${SPEAKER_COLORS.A}`,
                              }}
                              onClick={() =>
                                window.open(
                                  detailDialog.contract.signatureA.s3Url,
                                  "_blank",
                                )
                              }
                            >
                              <img
                                src={detailDialog.contract.signatureA.s3Url}
                                alt={`Unterschrift ${detailDialog.contract.personAName}`}
                                style={{
                                  maxWidth: 250,
                                  maxHeight: 120,
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                        {detailDialog.contract.signatureB && (
                          <Box>
                            <Typography
                              variant="body2"
                              color={colors.grey[300]}
                              mb={1}
                            >
                              {detailDialog.contract.personBName}
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: "#fff",
                                borderRadius: 2,
                                p: 1,
                                display: "inline-block",
                                cursor: "pointer",
                                border: `2px solid ${SPEAKER_COLORS.B}`,
                              }}
                              onClick={() =>
                                window.open(
                                  detailDialog.contract.signatureB.s3Url,
                                  "_blank",
                                )
                              }
                            >
                              <img
                                src={detailDialog.contract.signatureB.s3Url}
                                alt={`Unterschrift ${detailDialog.contract.personBName}`}
                                style={{
                                  maxWidth: 250,
                                  maxHeight: 120,
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default VoiceContracts;
